import { defineStore } from 'pinia'
import type { Country, Region } from '~/types/api'

type AutocompleteItem = {
  value: number
  text: string
}

export const useCountriesStore = defineStore('countries', () => {
  const api = useApi()
  const dataLoaded = ref(false)
  const countriesResponse = ref<Country[]>([])
  const regionsResponse = ref<Region[]>([])

  const countries = computed(() => {
    return countriesResponse.value.map((country: Country) => ({
      value: country.nid,
      text: country.native,
    }))
  })

  const regions = computed(() => {
    const regions = {} as Record<string, AutocompleteItem[]>

    regionsResponse.value.map((region: Region) => {
      const countryNid = region.countryId
      if (!countryNid) {
        return
      }
      if (regions[countryNid] === undefined || regions[countryNid] === null) {
        regions[countryNid] = []
      }
      regions[countryNid].push({
        value: region.nid,
        text: region.title ?? '',
      })
    })

    return regions
  })

  let countriesPromise: null | Promise<Country[] | undefined> = null
  let regionsPromise: null | Promise<Region[] | undefined> = null
  let initPromise: Promise<any>
  async function init() {
    if (initPromise) {
      return initPromise
    }

    dataLoaded.value = true
    countriesPromise = new Promise(async (resolve) => {
      await api.getCountries().then((res) => {
        countriesResponse.value = res ?? []
        resolve(res)
      })
    })
    regionsPromise = new Promise(async (resolve) => {
      await api.getRegions().then((res) => {
        regionsResponse.value = res ?? []
        resolve(res)
      })
    })

    initPromise = Promise.all([countriesPromise, regionsPromise])
    return initPromise
  }

  async function getCountryIdForCode(code?: string): Promise<Country | null> {
    if (!dataLoaded.value || countriesPromise === null || code === undefined) {
      // store not inited
      return null
    }

    await countriesPromise
    const foundResponse = Object.values(countriesResponse.value ?? {}).find(
      (country) => country.flagCode === code
    )
    if (foundResponse === undefined) {
      return null
    }
    return foundResponse
  }

  return {
    countriesResponse,
    countries,
    regionsResponse,
    regions,
    init,
    getCountryIdForCode,
  }
})
